import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../lib/api";
import { Overline, ProgressRing, Pill, Icon, PageLoader, gradFor } from "../../components/ui-kit";
import { cn } from "../../lib/utils";

const diffColor = { easy: "green", medium: "yellow", hard: "coral" };

export default function SubjectPage() {
  const { id } = useParams();
  const [subj, setSubj] = useState(null);
  useEffect(() => { api.get(`/academics/subject/${id}`).then(({ data }) => setSubj(data)); }, [id]);
  if (!subj) return <PageLoader />;

  const allTopics = subj.chapters.flatMap((c) => c.topics);
  const overall = Math.round(allTopics.reduce((a, t) => a + (t.progress || 0), 0) / Math.max(1, allTopics.length));

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <Link to="/learn" className="inline-flex items-center gap-1 text-sm text-mim-muted hover:text-mim-navy mb-4"><Icon name="ArrowLeft" className="w-4 h-4" />Subjects</Link>

      {/* Subject hero */}
      <div className="relative rounded-[28px] p-6 sm:p-7 text-white overflow-hidden mb-8" style={{ background: gradFor(subj.color) }}>
        <div className="absolute inset-0 dotgrid opacity-50" />
        <Icon name={subj.icon} className="absolute -right-4 -bottom-6 w-40 h-40 opacity-12" />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <Pill tone="white">{subj.chapters.length} chapters · {allTopics.length} topics</Pill>
            <h1 className="text-3xl sm:text-4xl font-bold mt-3 tracking-tight">{subj.name}</h1>
          </div>
          <ProgressRing value={overall} size={90} color="#ffffff" track="rgba(255,255,255,0.22)" label={`${overall}%`} sublabel="Done" labelClass="text-white" />
        </div>
      </div>

      {/* Chapters as journeys */}
      <div className="space-y-8">
        {subj.chapters.map((ch) => {
          const done = ch.topics.filter((t) => t.status === "mastered").length;
          const chPct = Math.round(ch.topics.reduce((a, t) => a + (t.progress || 0), 0) / Math.max(1, ch.topics.length));
          return (
            <div key={ch.id}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl text-white flex items-center justify-center font-bold text-sm shrink-0" style={{ background: gradFor(subj.color) }}>{ch.order}</div>
                <div className="flex-1">
                  <h3 className="font-bold text-mim-navy text-lg leading-tight">{ch.name}</h3>
                  <div className="text-xs text-mim-muted mt-0.5">{done}/{ch.topics.length} topics · {chPct}% complete</div>
                </div>
              </div>

              {/* vertical journey */}
              <div className="relative pl-2">
                <div className="absolute left-[19px] top-3 bottom-3 w-0.5 bg-mim-line" />
                <div className="space-y-2">
                  {ch.topics.map((t) => {
                    const st = t.status || "not_started";
                    const isDone = st === "mastered", isCurrent = st === "in_progress";
                    return (
                      <Link key={t.id} to={`/topic/${t.id}`} data-testid={`topic-link-${t.id}`} className="relative flex items-center gap-4 group">
                        <div className={cn("relative z-10 w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 transition-all",
                          isDone ? "bg-mim-green border-mim-green text-white" : isCurrent ? "bg-white border-mim-blue text-mim-blue shadow-md" : "bg-white border-mim-line text-mim-muted")}>
                          {isDone ? <Icon name="Check" className="w-4 h-4" /> : isCurrent ? <Icon name="Play" className="w-3.5 h-3.5 ml-0.5" /> : <span className="w-2 h-2 rounded-full bg-current" />}
                        </div>
                        <div className={cn("flex-1 rounded-2xl px-4 py-3 border transition-all group-hover:border-mim-blue/40 group-hover:shadow-mim",
                          isCurrent ? "bg-mim-soft border-mim-blue/20" : "bg-white border-mim-line")}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap"><h4 className={cn("font-semibold truncate", isDone ? "text-mim-muted" : "text-mim-navy")}>{t.name}</h4><Pill tone={diffColor[t.difficulty]}>{t.difficulty}</Pill></div>
                              {isCurrent && <div className="text-xs text-mim-blue font-medium mt-0.5">Continue · {t.progress}%</div>}
                            </div>
                            <Icon name="ChevronRight" className="w-5 h-5 text-mim-muted shrink-0 group-hover:translate-x-0.5 transition-transform" />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
